import { forwardRef } from 'react';
import { Flex, FlexProps } from '@cad-arc';
import { useInputSizeForApplicationType } from '../common/hooks/use-input-size-for-application-type';

type SpacerSize = 'sm' | 'md' | 'lg' | 'xl';

// Omit height because we don't allow setting explicit height here
interface FormSpacerProps extends Omit<FlexProps, 'height'> {
    size?: SpacerSize;
}

// these values are shortform for setting an arc spacing token, i.e height={6} is the same as height="arc-spacing-6"
// lg uses '2.5rem' directly because Arc's spacing scale does not define token 10
const SPACER_HEIGHT_MAP: Record<SpacerSize, number | string> = {
    sm: 'var(--arc-sizes-control-sm)',
    md: 'var(--arc-sizes-control-md)',
    lg: 'var(--arc-sizes-control-lg)',
    xl: 'var(--arc-sizes-control-xl)',
};

/**
 * Create a container that is the height of an input based on the application type (CAD or web).
 *
 * Used for when a child element is smaller than an input and you want to align it with the input.
 *
 * @component
 * @param {FormSpacerProps} props - Same as FlexProps, but excluding height
 * @return {ReactElement} The FormSpacer component.
 */

export const FormSpacer = forwardRef<HTMLDivElement, FormSpacerProps>(function FormSpacer(
    props,
    ref
) {
    const { size: propsSize, ...otherProps } = props;
    const size = useInputSizeForApplicationType(propsSize);
    const height = size ? SPACER_HEIGHT_MAP[size] : SPACER_HEIGHT_MAP.md;

    return <Flex ref={ref} height={height} {...otherProps} />;
});
